(function () {
  'use strict';

  const registry = new WeakMap();

  function init(root) {
    if (!root || registry.has(root)) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const gsap = window.gsap;
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reduceMotion = reduceMotionQuery.matches;
    const menuButton = root.querySelector('[data-pgc-menu]');
    const filterToggle = root.querySelector('[data-pgc-filter-toggle]');
    const filterSummary = root.querySelector('[data-pgc-filter-summary]');
    const productCount = root.querySelector('[data-pgc-product-count]');
    const filterPanel = root.querySelector('[data-pgc-filters]');
    const filterRail = root.querySelector('[data-pgc-filter-rail]');
    const filters = Array.from(root.querySelectorAll('[data-pgc-filter]'));
    const products = Array.from(root.querySelectorAll('[data-pgc-product]'));
    const newsletter = root.querySelector('[data-pgc-newsletter]');
    const empty = root.querySelector('[data-pgc-empty]');
    const previewForm = root.querySelector('[data-pgc-preview-form]');
    const newsletterForm = root.querySelector('.pgc-newsletter__form');
    const newsletterEmail = newsletterForm ? newsletterForm.querySelector('input[type="email"]') : null;
    const formError = newsletterForm ? newsletterForm.querySelector('[data-pgc-form-error]') : null;
    const initialFilter = filters.find(function (button) { return button.classList.contains('is-active'); });
    let requestedFilter = ((initialFilter && initialFilter.dataset.pgcFilter) || 'all').toLowerCase();
    let filterTimeline = null;
    let filterToken = 0;

    function syncFilterHeight() {
      if (!filterPanel) return;
      const height = filterPanel.getBoundingClientRect().height;
      if (height > 0) root.style.setProperty('--pgc-filter-height', height + 'px');
    }

    function syncFilterBoundary() {
      // The rail's travel is now pure CSS: it stretches the grid row and the panel
      // sticks inside it, so it releases at the last product without measuring
      // anything. This used to pin an explicit height that ended at the first
      // non-featured product, which is what cut the scroll short. Kept as a
      // clean-up so the old inline height does not survive a cached page.
      root.style.removeProperty('--pgc-filter-rail-height');
    }

    function syncFilterGeometry() {
      syncFilterHeight();
      syncFilterBoundary();
    }

    if (filterPanel) {
      syncFilterGeometry();
      window.addEventListener('resize', syncFilterGeometry, { signal });

      if ('ResizeObserver' in window) {
        const filterResizeObserver = new ResizeObserver(syncFilterGeometry);
        filterResizeObserver.observe(filterPanel);
        controller.signal.addEventListener('abort', function () { filterResizeObserver.disconnect(); }, { once: true });
      }
    }

    function closeMenu(returnFocus) {
      root.classList.remove('is-menu-open');
      if (menuButton) {
        menuButton.setAttribute('aria-expanded', 'false');
        menuButton.setAttribute('aria-label', 'Open menu');
        if (returnFocus) menuButton.focus();
      }
    }

    if (menuButton) {
      menuButton.addEventListener('click', function () {
        const open = root.classList.toggle('is-menu-open');
        menuButton.setAttribute('aria-expanded', String(open));
        menuButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      }, { signal });
    }

    if (filterToggle) {
      filterToggle.addEventListener('click', function () {
        const open = root.classList.toggle('is-filters-open');
        filterToggle.setAttribute('aria-expanded', String(open));
      }, { signal });
    }

    function matchingProducts(value) {
      return products.filter(function (product) {
        const haystack = (product.dataset.pgcSearch || '').toLowerCase();
        return value === 'all' || haystack.includes(value);
      });
    }

    function normalizeFilterMotion(reset) {
      if (!gsap) return;
      if (filterTimeline) {
        filterTimeline.kill();
        filterTimeline = null;
      }
      gsap.killTweensOf(products);
      if (reset) gsap.set(products, { clearProps: 'opacity,transform,visibility' });
    }

    function applyFilterState(button, matches) {
      filters.forEach(function (item) {
        item.classList.toggle('is-active', item === button);
        item.setAttribute('aria-pressed', String(item === button));
      });
      products.forEach(function (product) { product.hidden = !matches.includes(product); });
      if (empty) empty.hidden = matches.length !== 0;
      if (filterSummary) filterSummary.textContent = button.dataset.pgcLabel || button.textContent.trim();
      if (productCount) productCount.textContent = String(matches.length).padStart(2, '0');
    }

    function finishFilterLayout() {
      syncFilterGeometry();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    function closeMobileFilters() {
      if (!window.matchMedia('(max-width: 760px)').matches) return;
      root.classList.remove('is-filters-open');
      if (filterToggle) filterToggle.setAttribute('aria-expanded', 'false');
    }

    function selectFilter(button, options) {
      const value = (button.dataset.pgcFilter || 'all').toLowerCase();
      if (value === requestedFilter) return;
      const immediate = Boolean(options && options.immediate);

      const visibleProducts = products.filter(function (product) { return !product.hidden; });
      const matches = matchingProducts(value);
      const token = ++filterToken;
      requestedFilter = value;
      normalizeFilterMotion(immediate || reduceMotion || !gsap);
      closeMobileFilters();

      if (!gsap || reduceMotion || immediate) {
        applyFilterState(button, matches);
        finishFilterLayout();
        return;
      }

      const revealStagger = matches.length > 1
        ? Math.max(0, Math.min(.035, (.3 - .08 - .185) / (matches.length - 1)))
        : 0;
      filterTimeline = gsap.timeline({
        onComplete: function () {
          if (token !== filterToken) return;
          gsap.set(products, { clearProps: 'opacity,transform,visibility' });
          filterTimeline = null;
          finishFilterLayout();
        }
      });
      if (visibleProducts.length) {
        filterTimeline.to(visibleProducts, {
          autoAlpha: 0,
          duration: .08,
          ease: 'power2.out',
          y: -4
        }, 0);
      }
      filterTimeline
        .add(function () {
          if (token !== filterToken) return;
          applyFilterState(button, matches);
          const nonMatches = products.filter(function (product) { return !matches.includes(product); });
          gsap.set(nonMatches, { clearProps: 'opacity,transform,visibility' });
          gsap.set(matches, { autoAlpha: 0, y: 8 });
        }, .08)
        .to(matches, {
            autoAlpha: 1,
            duration: .185,
            ease: 'power4.out',
            stagger: revealStagger,
            y: 0
          }, .08);
    }

    filters.forEach(function (button) {
      button.addEventListener('click', function (event) {
        selectFilter(button, { immediate: event.detail === 0 });
      }, { signal });
      button.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        selectFilter(button, { immediate: true });
      }, { signal });
      button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
    });

    controller.signal.addEventListener('abort', function () {
      filterToken += 1;
      normalizeFilterMotion(true);
    }, { once: true });

    function setFormError(message) {
      if (!newsletterEmail || !formError) return;
      newsletterEmail.setAttribute('aria-invalid', message ? 'true' : 'false');
      formError.textContent = message;
      formError.hidden = !message;
    }

    function validateNewsletter() {
      if (!newsletterEmail) return true;
      if (newsletterEmail.validity.valueMissing) {
        setFormError('Enter your email to join the transmission.');
        return false;
      }
      if (newsletterEmail.validity.typeMismatch) {
        setFormError('That email signal is incomplete. Check the address and try again.');
        return false;
      }
      setFormError('');
      return true;
    }

    if (newsletterForm && newsletterEmail) {
      newsletterForm.addEventListener('submit', function (event) {
        if (!validateNewsletter()) {
          event.preventDefault();
          newsletterEmail.focus();
          return;
        }

        if (previewForm) {
          event.preventDefault();
          const status = previewForm.querySelector('[data-pgc-preview-status]');
          if (status) status.hidden = false;
          newsletterEmail.setAttribute('disabled', 'disabled');
          previewForm.querySelector('button').setAttribute('disabled', 'disabled');
        }
      }, { signal });

      newsletterEmail.addEventListener('input', function () {
        if (newsletterEmail.getAttribute('aria-invalid') === 'true') validateNewsletter();
      }, { signal });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.classList.contains('is-menu-open')) closeMenu(true);
    }, { signal });

    root.querySelectorAll('[data-pgc-nav-links] a').forEach(function (link) {
      link.addEventListener('click', function () { closeMenu(false); }, { signal });
    });

    if (!window.gsap && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
      root.classList.add('pgc-motion');
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
      products.concat(newsletter ? [newsletter] : []).forEach(function (item) { observer.observe(item); });
      controller.signal.addEventListener('abort', function () { observer.disconnect(); }, { once: true });
    }

    registry.set(root, controller);

    function refreshMotionPreference() {
      destroy(root);
      window.requestAnimationFrame(function () {
        if (root.isConnected) init(root);
      });
    }

    if (reduceMotionQuery.addEventListener) {
      reduceMotionQuery.addEventListener('change', refreshMotionPreference, { signal });
    } else if (reduceMotionQuery.addListener) {
      reduceMotionQuery.addListener(refreshMotionPreference);
      signal.addEventListener('abort', function () {
        reduceMotionQuery.removeListener(refreshMotionPreference);
      }, { once: true });
    }
  }

  function destroy(root) {
    const controller = registry.get(root);
    if (controller) controller.abort();
    registry.delete(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pgc-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    const root = event.target.querySelector('[data-pgc-root]');
    if (root) destroy(root);
  });
}());
